import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, WifiOff, Check } from "lucide-react";
import { count } from "@/lib/offlineQueue";
import { syncPendingActions } from "@/lib/offlineSync";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function SyncBanner() {
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  const refreshCount = useCallback(async () => {
    try {
      const c = await count();
      setPending(c);
    } catch {}
  }, []);

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 15000);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refreshCount]);

  // Auto-sync when coming back online (only trigger on online status change)
  const prevOnline = useRef(online);
  useEffect(() => {
    if (online && !prevOnline.current && pending > 0) {
      handleSync();
    }
    prevOnline.current = online;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const handleSync = async () => {
    if (syncing || !navigator.onLine) return;
    setSyncing(true);
    try {
      const { synced, failed } = await syncPendingActions();
      if (synced > 0) toast.success(`${synced} ação(ões) sincronizada(s)`);
      if (failed > 0) toast.error(`${failed} ação(ões) falharam`);
      await refreshCount();
    } catch {
      toast.error("Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  if (pending === 0) return null;

  return (
    <div className="bg-warning text-warning-foreground text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2">
      {online ? (
        <>
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
          <span>{pending} ação(ões) pendente(s)</span>
          <Button size="sm" variant="outline" className="h-6 text-xs ml-2" onClick={handleSync} disabled={syncing}>
            {syncing ? "Sincronizando..." : "Sincronizar agora"}
          </Button>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" />
          <span>{pending} ação(ões) salva(s) offline — serão sincronizadas ao reconectar</span>
        </>
      )}
    </div>
  );
}
